/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable @typescript-eslint/restrict-template-expressions */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-return */
import { Flex, SystemProps, Text, LegacyText } from "flicket-ui";
import flatten from "lodash/flatten";
import startCase from "lodash/startCase";
import { useRouter } from "next/router";
import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import styled from "styled-components";

import { SeatsioSeatingChart } from "@seatsio/seatsio-react";
import useSWR from "swr";
import {
  SeatSelectTypes,
  SeatsIOSeat,
} from "~components/reservation/constants";
import { seatsIOPublicKey } from "~config";
import { BaseContext, UserContext } from "~context";
import { getBookedSeatsData } from "~features/seated-reservation/graphql/queries";
import { getZoneKeys } from "~features/seated-reservation/helpers/zoneFilters";
import {
  getState,
  useReservationStore,
} from "~features/seated-reservation/store";
import * as actions from "~features/seated-reservation/store/actions/index";
import { QueryParams, Release } from "~features/seated-reservation/types";
import { EventQuery } from "~graphql/sdk";
import { getCategoryDataFromLabel, showToast } from "~lib/helpers";
import { SeatingChartOverlay } from "./SeatingChartOverlay";
import { useSeekaConverge } from "@seeka-labs/converge-react";

const SeatsWrapper = styled.div<SystemProps>`
  height: 100%;
  max-width: 100%;
  max-height: 293px;

  @media (min-width: ${(p) => p.theme.breakpoints.sm}) {
    max-height: 480px;
  }

  @media (min-width: ${(p) => p.theme.breakpoints.md}) {
    height: 100%;
    max-height: 100%;
  }
`;

interface SeatingChartWrapperProps {
  event: EventQuery["event"];
  release: Release;
  changeTickets: any[];
}

type SeatProps = {
  accessible: boolean;
  capacity: number;
  category: {
    accessible: boolean;
    label: string;
    color: string;
  };
  label: string;
  labels: {
    own: string;
    parent: string;
    section: string;
  };
  objectType: string;
  selectable: boolean;
  selected: boolean;
  status: string;
  id: string;
  uuid: string;
};

const renderObjectColor = (
  { selectable, objectType }: SeatProps,
  defaultColor: string
) => {
  return selectable && objectType === "Seat" ? "#AFCA54" : defaultColor;
};

const renderObjectIcon = (
  { label }: SeatProps,
  defaultIcon: string,
  extraConfig: any
) => {
  if (extraConfig.ownedSeats?.[label]) {
    return "user"; // https://fontawesome.com/v4.7.0/icon/user
  }

  return defaultIcon;
};

const onHoldTokenExpired = () => {
  actions.chartHoldTokenExpired();
};

const handleObjectClick = (object: SeatsIOSeat) => {
  actions.chartObjectClicked(object);
};

const chartJsUrl = process.env.SEATS_IO_CDN_URL || "";

export const SeatingChartWrapper = ({
  event,
  release,
  changeTickets,
}: SeatingChartWrapperProps) => {
  const converge = useSeekaConverge();
  const chartRef = useRef<any>(null);
  const router = useRouter();
  const { organization } = useContext(BaseContext);
  const { isAdmin, isPOS } = useContext(UserContext);
  const selectableZones = useReservationStore((state) => state.selectableZones);

  const venue = event.venue;
  const seatsEventKey = event.seatsEventKey;
  const seatsEventKeys = null;
  const channels = release.channels.filter((c) => c.isEnabled).map((c) => c.id);

  const { data: tooltipData, error: tooltipDataError } = useSWR(
    isAdmin && event?.id
      ? ["tooltipData", organization?.id, event?.id, "event"]
      : null,
    getBookedSeatsData
  );

  const tooltipDataLoading = !tooltipData && !tooltipDataError;

  const ownedSeats = useMemo(
    () =>
      changeTickets?.reduce((all, current) => {
        all[current?.seatLabel] = true;
        return all;
      }, {}),
    [changeTickets]
  );

  useEffect(() => {
    if (chartRef?.current?.chart) {
      chartRef?.current?.chart?.changeConfig({ extraConfig: { ownedSeats } });
    }
  }, [ownedSeats]);

  const handleChartRendered = async (chart) => {
    await actions.chartRendered({
      chart,
      release,
      releasedZones: release.releaseZones?.filter((zone) => zone.isActive),
      orgId: organization.id,
      seatsEventKey: event.seatsEventKey,
      ticketTypes: event.ticketTypes,
      multiBuyPromotions: event?.multiBuyPromotions,
      session: router.query.session,
      isAdmin,
      event: {
        id: event.id,
        title: event.title,
      },
      orgCurrency: organization.currency,
    });

    // Hack: David Nyika Road to the Title
    // Remove after 2024-08-18
    if (event.id === "19167bba-5ef4-4568-a470-4720f0be35bb") {
      await actions.updateOptions(
        {
          options: {
            amount: 10,
          },
        },
        converge
      );
    }

    // Hack: MEMO Music Hall
    if (organization.id === "9cc33f23-694a-40aa-9907-fb0cf92492b9") {
      await actions.updateOptions(
        {
          options: {
            seatSelectType: SeatSelectTypes.FREE_SELECT,
          },
        },
        converge
      );
    }

    // Hack: taupomp
    // Remove after 2025-04-30
    if (event.id === "817633b8-1ec0-46db-a2ea-3c87c54dbd70") {
      await actions.updateOptions(
        {
          options: {
            amount: 1,
          },
        },
        converge
      );
    }
  };

  const handleHoldSucceeded = async (seats: SeatsIOSeat[]) => {
    if (!seats.length) {
      showToast("Unable to select seats. Please select again.", "error");
      actions.unsetSeats();
      return;
    }

    void actions.userSelectedSeats({ seats }, converge);
  };

  const handleHoldFailed = (seat: SeatsIOSeat) => {
    showToast(
      "Selected seats are no longer available. Please select again.",
      "error"
    );
    actions.unsetSeats();
  };

  const handleReleaseHoldSucceeded = (seats: SeatsIOSeat | SeatsIOSeat[]) => {
    // This function appears to fire once for each seat that is released
    // and then once at the at the end with an array of the seats released.
    // we are going to use the array version as we want to perform an action when
    // all seats are released.
    if (Array.isArray(seats) && seats[0]?.objectType === "Seat") {
      actions.chartReleaseHold({
        seatLabels: seats.map((seat) => seat.label),
      });
    }
  };

  const handleValidSelection = (violations) => {
    actions.chartToggleValidSelection({ isValid: true, violations });
  };

  const handleInvalidSelection = (violations) => {
    actions.chartToggleValidSelection({ isValid: false, violations });
  };

  const renderTooltipInfo = useCallback(
    (object) => {
      if (object?.objectType === "GeneralAdmissionArea") {
        if (isAdmin) {
          return [
            `[b]Booked:[/b] ${object.numBooked}`,
            `[b]Remaining:[/b] ${object.numFree}`,
          ].join("[br/]");
        }

        if (+object.numFree === 0) {
          return "0 available places";
        }

        if (+object.numFree >= 10) {
          return;
        }

        const placesText = +object.numFree === 1 ? "place" : "places";

        return `${object.numFree} available ${placesText}`;
      }

      if (object?.objectType === "section") {
        if (+object.numberOfSelectableObjects === 0) {
          return "No seats available";
        }

        if (isAdmin) {
          return `[b]Remaining:[/b] ${object.numberOfSelectableObjects}`;
        }

        if (+object.numberOfSelectableObjects >= 10) {
          return;
        }

        const seatsText =
          +object.numberOfSelectableObjects === 1 ? "seat" : "seats";

        return `${object.numberOfSelectableObjects} available ${seatsText}`;
      }

      if (object?.objectType !== "Seat") {
        return;
      }

      const availableCategories = flatten(
        selectableZones?.map((z) => getZoneKeys(z)) || []
      );

      const { zone, section: sectionRaw } = getCategoryDataFromLabel(
        object?.category?.label as string
      );
      const labelParts = object?.label?.split("-");
      const sectionLabel = sectionRaw?.startsWith("Aisle")
        ? "Aisle"
        : "Section";
      const section = sectionRaw?.replace(/^(aisle|section) /i, "") || "-";
      const row = labelParts.length > 1 ? labelParts[1] : "-";
      const seat = labelParts.length > 2 ? labelParts[2] : "-";
      const stand = venue?.zones?.find(({ name }) => name === zone)?.stand;
      const customerDetails = object?.label && tooltipData?.[object.label];
      if (!customerDetails && process.env.NODE_ENV === "development") {
        const { eventId } = router.query;

        console.warn(
          `Couldn't find tooltip data for seat. This may mean there is no active ticket in database`,
          {
            eventId,
            seatLabel: object.label,
          }
        );
      }

      return [
        ...(isAdmin && tooltipDataLoading
          ? ["Loading booking data...[br/]"]
          : [customerDetails]),
        ...(object.status === "free" && !object.selectable
          ? [
              availableCategories?.includes(object?.category?.key)
                ? "Seat not available in selection[br/]"
                : "Seat not available in release[br/]",
            ]
          : []),
        `[b]Seat:[/b] ${seat}`,
        `[b]Row:[/b] ${row}`,
        `[b]${sectionLabel}:[/b] ${section}`,
        `${stand ? `[b]Stand:[/b] ${stand}` : ""}`,
        `[b]Zone:[/b] ${zone}[br/]`,
        `[b]Seat status:[/b] ${startCase(object.status as string)}`,
      ]
        .filter((value) => value)
        .join("[br/]");
    },
    [selectableZones, isAdmin, tooltipData, tooltipDataLoading]
  );

  useEffect(() => {
    if (chartRef?.current?.chart?.config?.tooltipInfo) {
      chartRef.current.chart.config.tooltipInfo = renderTooltipInfo;
    }
  }, [renderTooltipInfo]);

  const seatedStoreState = getState();

  // TODO: handle load failure
  const error = false;
  if (error) {
    return (
      <Flex
        height="100%"
        width="100%"
        maxHeight={{ _: "293px", sm: "480px", md: "100%" }}
        variant="center"
      >
        <LegacyText>Failed to load the seats map.</LegacyText>
      </Flex>
    );
  }

  // Loading state
  if (!seatsEventKey && !seatsEventKeys?.length) {
    return <SeatsWrapper />;
  }

  return (
    <>
      <SeatsWrapper>
        {useMemo(
          () => (
            <SeatsioSeatingChart
              ref={chartRef}
              event={seatsEventKey}
              events={seatsEventKeys}
              publicKey={seatsIOPublicKey}
              // Renders map initially with no categories
              availableCategories={[""]}
              objectTooltip={{
                showActionHint: false,
                showAvailability: false,
                showCategory: true,
                showLabel: false,
                showPricing: true,
                showUnavailableNotice: true,
                stylizedLabel: false,
              }}
              showActiveSectionTooltipOnMobile={true}
              onFilteredCategoriesChanged={(cat) => console.log(cat)}
              // Called for EVERY seat when the map is redrawn
              // E.g. movng the tooltip
              // objectColor={renderObjectColor}
              // This one appears slower
              // objectIcon={renderObjectIcon}
              // objectLabel={(object, defaultLabel, extraConfig) => {
              //   return object.labels.own;
              // }}
              onObjectClicked={handleObjectClick}
              // onObjectClicked={(object, selectedTicketType) => {
              //   console.log("onObjectClicked");
              //   console.log(object, selectedTicketType);
              //   // object.pulse();
              // }}
              legend={false}
              loading=""
              bookableObjectEvent={console.error}
              onObjectSelected={(object, selectedTicketType) => {
                // console.log("+++onObjectSelected");
                // console.log(object, selectedTicketType);
                // actions.setSeat({seat: object})
                // object.pulse();
              }}
              onBestAvailableSelected={(object, selectedTicketType) => {
                // console.log("+++onBestAvailableSelected");
                // console.log(object, selectedTicketType);
                // object.pulse();
              }}
              onObjectMouseOver={(object) => {
                // console.log("onObjectMouseOver");
                // console.log(object);
                // object.pulse();
              }}
              // showRowLines={true}
              // showSectionContents={"always"}
              onChartRendered={handleChartRendered}
              onHoldFailed={handleHoldFailed}
              onHoldSucceeded={handleHoldSucceeded}
              onObjectDeselected={handleReleaseHoldSucceeded}
              onReleaseHoldSucceeded={handleReleaseHoldSucceeded}
              onHoldTokenExpired={onHoldTokenExpired}
              onSelectionInvalid={handleInvalidSelection}
              onSelectionValid={handleValidSelection}
              session={
                router?.query?.session === QueryParams.SessionContinue
                  ? QueryParams.SessionContinue
                  : QueryParams.SessionStart
              }
              showFullScreenButton={false}
              chartJsUrl={chartJsUrl}
              extraConfig={{
                ownedSeats,
              }}
              tooltipInfo={renderTooltipInfo}
              selectionValidators={
                router.query?.ownedTickets
                  ? [{ type: "consecutiveSeats", ignoreCategories: true }]
                  : [
                      {
                        type: "noOrphanSeats",
                        ignoreCategories: true,
                        mode: "strict",
                      },
                    ]
              }
              multiSelectEnabled={
                (isAdmin || isPOS) &&
                seatedStoreState?.options?.seatSelectType ===
                  SeatSelectTypes.FREE_SELECT
              }
              channels={channels.length ? channels : undefined}
            />
          ),
          [
            isAdmin,
            isPOS,
            ownedSeats,
            seatedStoreState?.options?.seatSelectType,
          ]
        )}
      </SeatsWrapper>

      <SeatingChartOverlay />
    </>
  );
};
